import Spokes, {
    SpokesCallId,
    SpokesContact,
    SessionCallState,
} from 'resources/headsets/spokes';
import { PLUGIN_NAME } from 'resources/headsets/headset-utils';
import { addHeadsetLogEntry } from 'flow/utils/browser-utils';

const TO_HEADSET = 'ToHeadset';
const TONES = 'Unknown';

const CALL_EVENT_ARRAY_TYPE = 'CallStateArray';
const DEVICE_EVENT_ARRAY_TYPE = 'DeviceEventArray';

const EVENT_POLLING_RATE_MS = 2000;

export const connectToPlantronicsHub = (deviceInfo) => {
    return attachDevice(deviceInfo).then(registerPlugin).then(activatePlugin);
};

export const disconnectFromPlantronicsHub = () => {
    return unregister().then(release);
};

const applyCallbacksOnEvents = (callbacks) => (events) => {
    events.forEach((event) => {
        addHeadsetLogEntry(
            "Received event '" + event.eventName + "' from headset",
        );
        switch (event.eventName) {
            case 'MuteON': {
                callbacks.onMuteEvent();
                break;
            }
            case 'MuteOFF': {
                callbacks.onUnmuteEvent();
                break;
            }
            case 'AcceptCall': {
                callbacks.onAcceptCall(event.callId);
                break;
            }
            case 'TerminateCall': {
                callbacks.onHangup(event.callId);
                break;
            }
            default:
        }
    });
};

/**
 * We have to poll the Plantronics Hub for events.
 * There's two types of events
 *  - device events (like 'HeadsetButtonPressed', 'BaseStateChanged', etc)
 *  - call events (like 'AcceptCall', 'CallEnded', etc)
 */
export const pollEvents = (callbacks) => {
    if (!callbacks) {
        console.warn(
            "Callbacks to softphone not attached. Can't handle headset events",
        );
    }
    let headsetEventsPoller = setInterval(
        () =>
            Promise.all([
                pollDeviceEvents().then(mapToSimpleEvents),
                pollCallEvents().then(mapToSimpleEvents),
            ])
                // The above 'all'-promise resolves to an Array with two Arrays in it: [[deviceEvents], [callEvents]]
                .then((eventsArray) => {
                    const deviceEvents =
                        eventsArray[0] && eventsArray[0].length !== 0
                            ? eventsArray[0]
                            : [];
                    const callEvents =
                        eventsArray[1] && eventsArray[1].length !== 0
                            ? eventsArray[1]
                            : [];
                    const allEvents = deviceEvents.concat(callEvents);
                    return allEvents;
                })
                .then(applyCallbacksOnEvents(callbacks))
                .catch((e) => {
                    addHeadsetLogEntry(
                        'Plantronics polling error: ' + JSON.stringify(e),
                    );
                    callbacks.onDisconnect();
                    clearInterval(headsetEventsPoller);
                    headsetEventsPoller = null;
                }),
        EVENT_POLLING_RATE_MS,
    );
};

const pollDeviceEvents = () => {
    return new Promise((resolve, reject) => {
        Spokes.Device.events((result) => {
            if (checkIfPollingError(result)) {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

const pollCallEvents = () => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.callEvents(PLUGIN_NAME, (result) => {
            if (checkIfPollingError(result)) {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

const attachDevice = (deviceInfo) => {
    return new Promise((resolve, reject) => {
        Spokes.Device.attach(deviceInfo.Uid, (result) => {
            if (result.isError || !Spokes.Device.isAttached) {
                reject(result.Err);
            } else {
                resolve(result.Result);
            }
        });
    });
};

const registerPlugin = () => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.register(PLUGIN_NAME, (result) => {
            if (result.isError && result.Err.Description !== 'Plugin exists') {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

const activatePlugin = () => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.isActive(PLUGIN_NAME, true, (result) => {
            if (result.isError) {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

const unregister = () => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.unRegister(PLUGIN_NAME, (result) => {
            if (result.isError) {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

const release = () => {
    return new Promise((resolve, reject) => {
        Spokes.Device.release((result) => {
            if (result.isError) {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

export const signalIncomingCall = (callId) => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.incomingCall(
            PLUGIN_NAME,
            spokesCallId(callId),
            spokesContact('Dummy Contact'),
            TONES,
            TO_HEADSET,
            (result) => {
                if (result.isError) {
                    reject(result);
                } else {
                    resolve(result);
                }
            },
        );
    });
};

export const signalOutgoingCall = (callId) => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.outgoingCall(
            PLUGIN_NAME,
            spokesCallId(callId),
            spokesContact('Dummy Contact'),
            TO_HEADSET,
            (result) => {
                if (result.isError) {
                    reject(result);
                } else {
                    resolve(result);
                }
            },
        );
    });
};

export const signalAnsweringCall = (callId) => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.answerCall(
            PLUGIN_NAME,
            spokesCallId(callId),
            (result) => {
                if (result.isError) {
                    reject(result);
                } else {
                    resolve(result);
                }
            },
        );
    });
};

export const signalEndingCall = (callId) => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.terminateCall(
            PLUGIN_NAME,
            spokesCallId(callId),
            (result) => {
                if (result.isError) {
                    reject(result);
                } else {
                    resolve(result);
                }
            },
        );
    });
};

export const signalHoldingCall = (callId) => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.holdCall(PLUGIN_NAME, spokesCallId(callId), (result) => {
            if (result.isError) {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

export const signalResumingCall = (callId) => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.resumeCall(
            PLUGIN_NAME,
            spokesCallId(callId),
            (result) => {
                if (result.isError) {
                    reject(result);
                } else {
                    resolve(result);
                }
            },
        );
    });
};

export const signalMutingCall = () => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.muteCall(PLUGIN_NAME, true, (result) => {
            if (result.isError) {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

export const signalUnmutingCall = () => {
    return new Promise((resolve, reject) => {
        Spokes.Plugin.muteCall(PLUGIN_NAME, false, (result) => {
            if (result.isError) {
                reject(result);
            } else {
                resolve(result);
            }
        });
    });
};

/**
 * Spokes spits out responses that vary somewhat between call- and device-events.
 * The responses also contain some uninteresting stuff.
 * This method takes a response and puts it in simpler blob.
 */
const mapToSimpleEvents = ({ Result, Type_Name }) => {
    const events = Result;
    const isCallEventsArray = Type_Name === CALL_EVENT_ARRAY_TYPE;
    const isDeviceEventsArray = Type_Name === DEVICE_EVENT_ARRAY_TYPE;
    return events.map((event) => {
        // If we have a call event, we'll add info on call state and call id.
        const eventInfo = {};
        if (isCallEventsArray) {
            eventInfo.eventName = SessionCallState.Lookup[event.Action];
            eventInfo.callId = event.CallId.Id;
        } else if (isDeviceEventsArray) {
            eventInfo.eventName = event.Event_Name;
        }
        return eventInfo;
    });
};

const checkIfPollingError = (result) => {
    if (!result.isError) {
        return false;
    }
    return (
        result.Err.Description ===
            'No response.  Server appears to be offline.' ||
        result.Err.Description === 'Invalid session id' ||
        result.Err.Description === 'Empty session id'
    );
};

const DUMMY_CALL_ID = '1';

// Sending callId to the headsets introduced this bug: https://telavox.atlassian.net/browse/WEB-10067
// If we need to send the callId in the future to support headset controls for multiple calls we need
// to make sure we don't introduce that bug again.
// For now we send a DUMMY_CALL_ID (1) as we did before.
const spokesCallId = (/*callId*/) => new SpokesCallId({ Id: DUMMY_CALL_ID });

const spokesContact = (contactName) => new SpokesContact({ Name: contactName });
